/*
 * „Benutzer hinzufügen“ (docs/registrierportal.md): ein Knopf, der einen
 * QR-Code mit einer kurzlebigen, einmal verwendbaren Einladung zeigt. Das
 * Token holt die lokale Laufzeit über ihren Field-Client, der sich beim
 * Server mit seinem Gerätezertifikat ausweist.
 *
 * Nur am Bedienfeld. Die Web-HMI entfernt den Baustein schon vor dem
 * Zeichnen (hmi/localOnly.ts); ohne `createInvitation` im Laufzeitkontext
 * (Vorschau in HVACloud) steht nur ein Hinweis da.
 */

import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import type { UserInviteWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { useHmiRuntime, type PanelInvitation } from './runtimeContext';
import { QrCode } from './QrCode';
import './userInvite.css';

type State =
  | { phase: 'closed' }
  | { phase: 'loading' }
  | { phase: 'shown'; invitation: PanelInvitation }
  | { phase: 'error'; message: string };

export function UserInviteRenderer({ widget }: { widget: UserInviteWidget; source: IPointBindingSource }) {
  const runtime = useHmiRuntime();
  const [state, setState] = useState<State>({ phase: 'closed' });
  const label = widget.buttonText?.trim() || 'Benutzer hinzufügen';

  if (runtime.editing || !runtime.createInvitation) {
    return (
      <div class="hv-userinvite">
        <button type="button" class="hv-userinvite__btn" disabled>{label}</button>
        <span class="hv-userinvite__note">Nur am Bedienfeld</span>
      </div>
    );
  }

  const open = async () => {
    setState({ phase: 'loading' });
    try {
      const invitation = await runtime.createInvitation!();
      setState({ phase: 'shown', invitation });
    } catch (e) {
      setState({ phase: 'error', message: e instanceof Error ? e.message : 'Keine Verbindung zur Cloud.' });
    }
  };

  return (
    <div class="hv-userinvite">
      <button type="button" class="hv-userinvite__btn" onClick={() => void open()}>{label}</button>
      {/* Ins Dokument gehaengt: die freie Buehne skaliert per transform, und
          darin waere ein fixierter Dialog mitskaliert und abgeschnitten. */}
      {state.phase !== 'closed' && createPortal(
        <div class="hv-userinvite__overlay" role="dialog" aria-modal="true" aria-label={label}>
          <div class="hv-userinvite__card">
            <h2 class="hv-userinvite__title">{label}</h2>
            {state.phase === 'loading' && <p>QR-Code wird geholt …</p>}
            {state.phase === 'error' && <p class="hv-userinvite__error">{state.message}</p>}
            {state.phase === 'shown' && (
              <InvitationView invitation={state.invitation} hint={widget.hint} onRenew={() => void open()} />
            )}
            <button type="button" class="hv-userinvite__close" onClick={() => setState({ phase: 'closed' })}>Schließen</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function InvitationView({ invitation, hint, onRenew }: { invitation: PanelInvitation; hint?: string; onRenew: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const left = Math.max(0, Math.floor((Date.parse(invitation.expiresAt) - now) / 1000));
  if (left === 0) {
    return (
      <>
        <p>Der Code ist abgelaufen.</p>
        <button type="button" class="hv-userinvite__btn" onClick={onRenew}>Neuen Code anzeigen</button>
      </>
    );
  }
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  return (
    <>
      <QrCode text={invitation.url} label="QR-Code zur Registrierung" />
      <p class="hv-userinvite__hint">
        {hint?.trim() || 'Mit der Handykamera scannen und das Formular ausfüllen. Der Code gilt nur für eine Person.'}
      </p>
      <p class="hv-userinvite__left">Gültig noch {mm}:{ss}</p>
    </>
  );
}
